import React, { useState, useEffect } from 'react';
import { Award, Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

interface LoginFormProps {
  sessionError?: string | null;
  /** True while the login page slides away after a successful sign-in. */
  leaving?: boolean;
}

export function LoginForm({ sessionError, leaving = false }: LoginFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { signIn, clearSessionError } = useAuth();

  // Set session error as the initial error if provided
  useEffect(() => {
    if (sessionError) {
      setError(sessionError);
      clearSessionError();
    }
  }, [sessionError, clearSessionError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const { error } = await signIn(email, password);
      if (error) {
        if (error.message.includes('Supabase is not configured')) {
          setError('Please set up Supabase connection. Click "Connect to Supabase" in the top right or check your .env file.');
        } else {
          setError(error.message);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex overflow-hidden ${leaving ? 'pointer-events-none' : ''}`}>
      {/* Left Side - Login Form */}
      <div className={`w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 lg:p-20 bg-white dark:bg-slate-950 ${leaving ? 'animate-slide-out-left' : ''}`}>
        <div className="w-full max-w-sm space-y-10">
          {/* Logo and Header */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <img 
                src="/images/logo.png" 
                alt="Rapid Cert Logo" 
                className="h-8 w-auto object-contain"
                onError={(e) => {
                  console.error('Logo failed to load');
                  e.currentTarget.style.display = 'none';
                }}
                onLoad={() => console.log('Logo loaded successfully')}
              />
              <h1 className="text-lg font-semibold text-slate-900 dark:text-white">
                Rapid Cert
              </h1>
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold text-slate-900 dark:text-white tracking-tight">
                Welcome back
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Enter your credentials to continue
              </p>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="p-3.5 bg-red-50 dark:bg-red-950/40 border border-red-200/50 dark:border-red-900/30 rounded-lg flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-400 leading-relaxed">{error}</p>
            </div>
          )}

          {/* Login form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                Email
              </label>
              <div className="relative group">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-slate-900 dark:group-focus-within:text-white transition-colors pointer-events-none" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 dark:border-slate-800 rounded-lg bg-white dark:bg-slate-900/50 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-900 dark:focus:ring-slate-400 focus:border-slate-900 dark:focus:border-slate-400 transition-all disabled:opacity-50"
                  placeholder="you@company.com"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="block text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                Password
              </label>
              <div className="relative group">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-slate-900 dark:group-focus-within:text-white transition-colors pointer-events-none" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 text-sm border border-slate-200 dark:border-slate-800 rounded-lg bg-white dark:bg-slate-900/50 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-900 dark:focus:ring-slate-400 focus:border-slate-900 dark:focus:border-slate-400 transition-all disabled:opacity-50"
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors p-1"
                  disabled={isLoading}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || !email || !password}
              className="w-full py-2.5 px-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-medium rounded-lg hover:bg-slate-800 dark:hover:bg-slate-100 active:bg-slate-900 dark:active:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-6"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin"></div>
                  <span>Signing in...</span>
                </>
              ) : (
                <span>Sign In</span>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="pt-6">
            <p className="text-xs text-center text-slate-400 dark:text-slate-600">
              Secure authentication
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Visual Hero */}
      <div className={`hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-900 ${leaving ? 'animate-slide-out-right' : ''}`}>
        {/* Background Image */}
        <img 
          src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
          alt="Professional training and certification"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-slate-900/75"></div>

        {/* Subtle Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>

        {/* Gradient Accents */}
        <div className="absolute top-1/4 -right-32 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 -left-32 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl"></div>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full p-20">
          <div className="max-w-md space-y-8 text-center">
            {/* Tagline */}
            <div className="space-y-5">
              <h2 className="text-3xl font-semibold tracking-tight leading-tight text-white drop-shadow-lg">
                Certificate Management Made Simple
              </h2>
              <p className="text-base text-slate-200 leading-relaxed drop-shadow">
                Streamline your training operations with automated certificate generation, client management, and compliance tracking
              </p>
            </div>

            {/* Feature Pills */}
            <div className="flex items-center justify-center gap-2.5 flex-wrap pt-4">
              <div className="px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 text-xs text-white font-medium shadow-sm">
                <Award className="w-3 h-3 inline mr-1.5 -mt-0.5" />
                Certified
              </div>
              <div className="px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 text-xs text-white font-medium shadow-sm">
                <Lock className="w-3 h-3 inline mr-1.5 -mt-0.5" />
                Secure
              </div>
              <div className="px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 text-xs text-white font-medium shadow-sm">
                Efficient
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}